import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import * as dateFns from "date-fns";
import { CalendarHeader, CalendarCells, DayLabels } from "@/components";
import AppliedDatesList from "./AppliedDatesList";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import {
  format,
  subMonths,
  addMonths,
  isBefore,
  isEqual,
  startOfDay
} from "date-fns";
import {
  useGetAppliedToQuery,
  useGetAbsentPresentQuery,
  useGetAbsentPresentListQuery,
  useGetQuickAddQuery,
} from "@/api/services/attendance/regularization/EmployeeRegularization";
import {
  useGetPayrollLockDateInfoQuery,
  useGetPayrollLockDateQuery,
} from "@/api/services/attendance/calendar/Calendar";
import { toast } from "@/components/toastify";
const RegularizeCalendar = ({ pendingRecords }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [attendanceTime, setAttendanceTime] = useState([]);
  const [attendanceArray, setAttendanceArray] = useState([]);

  const attendanceStartDate = pendingRecords?.data?.lockStartDate;
  const attendanceCloseDate = pendingRecords?.data?.lockEndDate;

  const { data: appliedToDetails = [] } = useGetAppliedToQuery();
  const appliedToData = appliedToDetails?.data || [];

  const managers = [
    {
      name: appliedToData?.primary?.primaryManagerName,
      id: appliedToData?.primary?.primaryManagerId,
    },
    {
      name: appliedToData?.reviewer?.reviewerManagerName,
      id: appliedToData?.reviewer?.reviewerManagerId,
    },
  ];

  const [yearMonth, setYearMonth] = useState(format(new Date(), "MM-yyyy"));
  const { data: lockDate, refetch: refetchLockInfo } =
    useGetPayrollLockDateQuery({ yearMonth: yearMonth });

  const getLockMonth = useMemo(() => {
    // if (!lockDate?.data) return null;
    if (!lockDate?.data?.payrollMonth) return yearMonth;
    const { startDate, endDate } = lockDate.data.payrollMonth;
    const end = startOfDay(new Date(endDate));
    const presentDate = startOfDay(new Date(currentMonth));
    const nextMonth = new Date(currentMonth);
    nextMonth.setDate(1);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const currentLockMonth = format(presentDate, "MM-yyyy");
    const nextLockMonth = format(nextMonth, "MM-yyyy");
    // return presentDate >= end ? currentLockMonth : nextLockMonth;
    return isBefore(presentDate, end) || isEqual(presentDate, end)
        ? currentLockMonth
        : nextLockMonth;
  }, [lockDate, currentMonth, yearMonth]);
  const { data: isLocked, refetch: refetchPayrollLockDate } =
    useGetPayrollLockDateInfoQuery(
      { yearMonth: getLockMonth },
      {
        skip: !getLockMonth,
      }
    );
  const lockDateRecords = isLocked?.data || [];

  const { data: absentPresent = [], refetch: refetchAbsentPresent } =
    useGetAbsentPresentQuery(
      { yearMonth: getLockMonth },
      { skip: !getLockMonth }
    );
  const absentPresentData = absentPresent?.data || [];

  const { data: absentPresentList = [], refetch: refetchAbsentPresentList } =
    useGetAbsentPresentListQuery(
      { yearMonth: getLockMonth },
      { skip: !getLockMonth }
    );
  const absentPresentListData = absentPresentList?.data || [];

  const { data: quickAddList = [], refetch: refetchQuickAdd } =
    useGetQuickAddQuery({ yearMonth: getLockMonth }, { skip: !getLockMonth });
  const quickAddListData = quickAddList?.data?.attendanceInfo || [];

  useEffect(() => {
    if (lockDateRecords?.payrollMonth) {
      const { startDate: rawStartDate, endDate: rawEndDate } =
        lockDateRecords?.payrollMonth;

      if (rawStartDate && rawEndDate) {
        setStartDate(new Date(rawStartDate));
        setEndDate(new Date(rawEndDate));
        return;
      }
    }

    // Fallback if payrollMonth is missing
    const [month, year] = yearMonth.split("-"); // e.g., "04-2025" → ["04", "2025"]
    const selectedDate = new Date(`${year}-${month}-01`);

    const defaultStart = new Date(selectedDate);
    defaultStart.setMonth(defaultStart.getMonth() - 1); // Previous month
    defaultStart.setDate(25);

    const defaultEnd = new Date(selectedDate);
    defaultEnd.setDate(24);

    setStartDate(defaultStart);
    setEndDate(defaultEnd);
  }, [lockDateRecords, yearMonth]);

  const handlePrevMonth = () => {

    let monthYear = lockDateRecords?.monthYear;
    if (!monthYear) return;
    const [month, year] = monthYear.split("-").map(Number);
    const val = new Date(year, month - 1, 1);
    const newMonth = subMonths(val, 1);
    const updatedMonth = format(newMonth, "MM-yyyy");
    setCurrentMonth(newMonth);
    setYearMonth(updatedMonth);
    setSelectedDates([]);
    refetchPayrollLockDate();
    refetchLockInfo();
    refetchAbsentPresent();
    refetchAbsentPresentList();
    refetchQuickAdd();
  };

  const handleNextMonth = () => {
    let monthYear = lockDateRecords?.monthYear;
    if (!monthYear) return;
    const [month, year] = monthYear.split("-").map(Number);
    const val = new Date(year, month - 1, 1);
    const newMonth = addMonths(val, 1);
    const updatedMonth = format(newMonth, "MM-yyyy");
    setCurrentMonth(newMonth);
    setYearMonth(updatedMonth);
    setSelectedDates([]);
    refetchPayrollLockDate();
    refetchLockInfo();
    refetchAbsentPresent();
    refetchAbsentPresentList();
    refetchQuickAdd();
  };

  useEffect(() => {
    if (!absentPresentData || !absentPresentData.attendanceData) return;

    // Format date and find attendance record
    const formattedDate = dateFns.format(new Date(), "yyyyMMdd");
    const attendance = absentPresentData.attendanceData.find(
      (entry) => entry.attendanceDate === formattedDate
    );

    setAttendanceTime(attendance ? [attendance] : []);
  }, [absentPresentData]);

  const restrictedDates = [
    ...(absentPresentListData.regPendingList || []),
    ...(absentPresentListData.presentList || []),
    ...(absentPresentListData.holidayList || []),
    ...(absentPresentListData.regApprovedList || []),
    ...(absentPresentListData.weekendPolicyList || []),
    ...(absentPresentListData.yesterDayCutOffList || []),
  ];

  const restrictedLeaveDates = absentPresentListData.leaveListSession;

  const onDateClick = (day) => {
    const formattedDate = format(day, "yyyy-MM-dd");
    const today = format(new Date(), "yyyy-MM-dd");

    // Convert attendanceStartDate and attendanceCloseDate to Date objects
    const startDate = new Date(attendanceStartDate);
    const closeDate = new Date(attendanceCloseDate);
    const selectedDate = new Date(formattedDate);

    // Check if the selected date is within the allowed range
    if (selectedDate < startDate || selectedDate > closeDate) {
      toast("error", "Attendance is closed for this date!");
      return;
    }

    const absentList = absentPresentListData?.absentList || [];
    if (!absentList.includes(formattedDate)) {
      // If date is not absent, don't open
      return;
    }

    // Check if the date is restricted
    if (restrictedDates.includes(formattedDate) || formattedDate === today) {
      return;
    }

    const restrictedSessions = restrictedLeaveDates[formattedDate] || [];

    const isFullyRestricted =
      restrictedSessions.includes("Session 1") &&
      restrictedSessions.includes("Session 2");

    if (isFullyRestricted) {
      toast("error", "Leave has already been applied for this date!");
      return;
    }

    const dojList = absentPresentListData.dojList || [];

    const isBeforeDOJ = dojList.some((dojDate) => {
      const doj = new Date(dojDate);
      return selectedDate < doj;
    });

    if (isBeforeDOJ) {
      // toast("error", "Cannot mark attendance before Date of Joining!");
      return;
    }

    //  Safe check for absentPresentData & attendanceData
    const attendanceList = absentPresentData?.attendanceData;
    if (!attendanceList || !Array.isArray(attendanceList)) {
      // console.warn("Attendance data not available for this date.");
      return;
    }

    const attendance = attendanceList.filter(
      (entry) => entry.attendanceDate === formattedDate
    );

    // const attendance = absentPresentData?.attendanceData?.filter(
    //   (entry) => entry.attendanceDate === formattedDate
    // );

    setAttendanceTime((prevAttendance) => {
      const updatedAttendance =
        attendance.length > 0
          ? [...prevAttendance, ...attendance] // Spread attendance array directly
          : prevAttendance; // Keep previous state if no data found

      return updatedAttendance;
    });

    setSelectedDates((prevDates) =>
      prevDates.some((date) => dateFns.isSameDay(date, day))
        ? prevDates.filter((date) => !dateFns.isSameDay(date, day))
        : [...prevDates, day]
    );

    // Attendance array update without duplicates
    setAttendanceArray((prevAttendance) => {
      const isDuplicate = prevAttendance.some(
        (entry) => entry.attendanceDate === formattedDate
      );

      if (isDuplicate) {
        return prevAttendance; // Prevent duplicates
      } else {
        return [
          ...prevAttendance,
          { attendanceDate: formattedDate, ...attendance },
        ];
      }
    });

    setIsCardOpen(true);
  };

  const quickAddcal = () => {
    if (!quickAddListData || quickAddListData.length === 0) {
      return;
    }

    const newDates = quickAddListData.map((entry) =>
      dateFns.parse(entry.attendanceDate, "yyyy-MM-dd", new Date())
    );

    setAttendanceTime((prevAttendance) => {
      const updatedAttendance =
        quickAddListData.length > 0
          ? [...prevAttendance, ...quickAddListData] // Directly add full attendance objects
          : prevAttendance; // Keep previous state if no data found

      return updatedAttendance;
    });

    setSelectedDates((prevDates) => {
      const uniqueDates = [
        ...prevDates,
        ...newDates.filter(
          (newDate) =>
            !prevDates.some((date) => dateFns.isSameDay(date, newDate))
        ),
      ];
      return uniqueDates;
    });

    setAttendanceArray((prevAttendance) => {
      const updatedAttendance = [...prevAttendance];

      quickAddListData.forEach((entry) => {
        const formattedDate = entry.attendanceDate;

        const existing = prevAttendance.some(
          (att) => att.attendanceDate === formattedDate
        );

        if (!existing) {
          updatedAttendance.push({
            attendanceDate: formattedDate,
            inTime: entry.inTime,
            outTime: entry.outTime,
            // halfDay: "13:30" ,
            halfDay: entry.halfDay,
          });
        }
      });

      return updatedAttendance;
    });

    setIsCardOpen(true); // Open details for the first date
  };

  const handleDelete = (id, applydatearr, index) => {
    // Filter out the selected date
    const updatedSelectedDates = selectedDates.filter(
      (date) => dateFns.format(date, "yyyyMMdd") !== id
    );
    // Ensure applydatearr is properly formatted before filtering
    const formattedApplyDate = Array.isArray(applydatearr)
      ? applydatearr.join(",")
      : applydatearr;
    const updatedAttendanceArray = attendanceTime.filter(
      (_, idx) => idx !== index
    );
    // Update the state
    setSelectedDates(updatedSelectedDates);
    setAttendanceTime(updatedAttendanceArray);
  };

  // const formattedDates = selectedDates.map((date) => ({
  //   applydatearr: dateFns.format(date, "yyyy-MM-dd"),
  //   date: dateFns.format(date, "dd"),
  //   month: dateFns.format(date, "MMM"),
  //   inTime: "",
  //   outTime: "",
  //   ActualinTime: "",
  //   ActualoutTime: "",
  //   reason: "",
  //   id: dateFns.format(date, "yyyyMMdd"),
  //   sessions: "",
  //   halfDay: "",
  // }));

  const formattedDates = selectedDates.map((date) => {
    const dateStr = dateFns.format(date, "yyyy-MM-dd");
    return {
      applydatearr: dateStr,
      date: dateFns.format(date, "dd"),
      month: dateFns.format(date, "MMM"),
      inTime: "",
      outTime: "",
      ActualinTime: "",
      ActualoutTime: "",
      reason: "",
      id: dateFns.format(date, "yyyyMMdd"),
      sessions: "",
      halfDay: "",
      restrictedSessions: restrictedLeaveDates[dateStr] || [], // 👈 Add this line
      workType:"",
    };
  });

  const handleButtonClick = () => {
    setIsCardOpen((prev) => !prev);
    setSelectedDates([]);
  };

  const formattedDate = format(currentMonth, "yyyy-MM-dd");
  const startDate1 = new Date(attendanceStartDate);
  const closeDate1 = new Date(attendanceCloseDate);
  const selectedDate1 = new Date(formattedDate);

  // Check if selectedDate is in allowed range
  const isDateWithinRange =
    selectedDate1 >= startDate1 && selectedDate1 <= closeDate1;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Box
          sx={{
            borderRadius: 1,
            boxShadow: "0px 4px 14px 0px #EAF5EE",
          }}
        >
          <CalendarHeader
            currentMonth={lockDateRecords?.monthYear}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
          />
          <DayLabels startDate={startDate} />
          <CalendarCells
            startDate={startDate}
            endDate={endDate}
            currentDate={new Date()}
            selectedDates={selectedDates}
            onDateClick={onDateClick}
            // quickaddcal={overallquickadd}
            holidayList={absentPresentListData.holidayList}
            presentList={absentPresentListData.presentList}
            regApprovedList={absentPresentListData.regApprovedList}
            regPendingList={absentPresentListData.regPendingList}
            absentList={absentPresentListData.absentList}
            weekendPolicyList={absentPresentListData.weekendPolicyList}
            leaveListSession={absentPresentListData.leaveListSession}
            // satSunList={dummyData.satSunList}
            // halfDayList={dummyData.halfDayList}
            // leaveList={dummyData.leaveList}
            // absentList={dummyData.absentList}
            // regRejectedList={dummyData.regRejectedList}
            // absentListInAndOut={dummyData.absentListInAndOut}

            absentPresentData={absentPresentData}
          />

          <Box mt={3}>
            {absentPresentListData?.absentList?.length > 0 ? (
              <Box display="flex" alignItems="center" className="regularizeDay">
                <PriorityHighIcon style={{ color: "red", marginRight: 8 }} />
                <Typography variant="body2">
                  <span className="expectioncount">
                    {absentPresentListData?.absentList?.length}
                  </span>{" "}
                  exception days.{" "}
                  {isDateWithinRange && (
                    <Typography
                      variant="body2"
                      component="span"
                      color="primary"
                      style={{ cursor: "pointer" }}
                      onClick={quickAddcal}
                    >
                      Quick Add
                    </Typography>
                  )}
                </Typography>
              </Box>
            ) : (
              ""
            )}
          </Box>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        md={8}
        sx={{
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        {isCardOpen && (
          <AppliedDatesList
            appliedDates={formattedDates}
            managers={managers}
            attendanceTime={attendanceTime}
            isCardOpen={isCardOpen}
            handleButtonClick={handleButtonClick}
            setIsCardOpen={setIsCardOpen}
            handleDelete={handleDelete}
            attendanceArray={attendanceArray}
            getLockMonth={getLockMonth}
            restrictedLeaveDates={restrictedLeaveDates}
          />
        )}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            mb: 2,
          }}
        >
          {!isCardOpen && (
            <>
              <Typography
                variant="h6"
                sx={{
                  textAlign: "center",
                  p: (theme) => theme.spacing(2, 2),
                }}
              >
                Select date to start regularizing
              </Typography>
              {isDateWithinRange &&
                absentPresentListData?.absentList?.length > 0 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={quickAddcal}
                    sx={{ mt: 2 }}
                  >
                    Quick Add ({absentPresentListData?.absentList?.length})
                  </Button>
                )}
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default RegularizeCalendar;
